import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminTenants, useAdminTenantUsers, useResetUserPassword, useUnlockUser } from '@/hooks/useAdmin';
import { TenantUser } from '@/services/admin';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import toast from 'react-hot-toast';
import {
  Users,
  Building2,
  Search,
  Shield,
  User as UserIcon,
  Stethoscope,
  DollarSign,
  Calendar,
  Mail,
  KeyRound,
  Copy,
  AlertTriangle,
  FlaskConical,
  Unlock,
} from 'lucide-react';

const roleIcons: Record<string, React.ReactNode> = {
  superadmin: <Shield className="h-4 w-4" />,
  admin: <Shield className="h-4 w-4" />,
  vet: <Stethoscope className="h-4 w-4" />,
  cashier: <DollarSign className="h-4 w-4" />,
  employee: <UserIcon className="h-4 w-4" />,
  laboratory: <FlaskConical className="h-4 w-4" />,
};

const roleLabels: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Administrador',
  vet: 'Veterinario',
  cashier: 'Cajero',
  employee: 'Empleado',
  laboratory: 'Laboratorio',
};

const roleColors: Record<string, string> = {
  superadmin: 'bg-purple-100 text-purple-700',
  admin: 'bg-indigo-100 text-indigo-700',
  vet: 'bg-blue-100 text-blue-700',
  cashier: 'bg-green-100 text-green-700',
  employee: 'bg-gray-100 text-gray-700',
  laboratory: 'bg-emerald-100 text-emerald-700',
};

export default function AdminTenantUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTenantId = searchParams.get('tenant') ? Number(searchParams.get('tenant')) : null;

  const { data: tenants, isLoading: loadingTenants } = useAdminTenants();
  const { data: users, isLoading: loadingUsers } = useAdminTenantUsers(selectedTenantId);
  const resetPassword = useResetUserPassword();
  const unlockUser = useUnlockUser(selectedTenantId ?? undefined);

  const [search, setSearch] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [resetResult, setResetResult] = useState<{
    email_sent: boolean;
    temporary_password: string | null;
    user_email: string;
  } | null>(null);

  const selectedTenant = tenants?.find((t) => t.id === selectedTenantId);

  const filteredUsers = users?.filter(
    (user) =>
      user.full_name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleUnlock = async (user: TenantUser) => {
    try {
      const r = await unlockUser.mutateAsync(user.id);
      toast.success(
        r.was_locked
          ? `${r.user_name} ya puede volver a entrar`
          : `${r.user_name} no estaba bloqueado`,
      );
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'No se pudo desbloquear');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      const result = await resetPassword.mutateAsync(selectedUser.id);
      setResetResult({
        email_sent: result.email_sent,
        temporary_password: result.temporary_password,
        user_email: result.user_email,
      });
      if (result.email_sent) {
        toast.success(`Se envió la nueva contraseña a ${result.user_email}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al resetear la contraseña');
      setShowResetModal(false);
    }
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setSelectedUser(null);
    setResetResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios por Cliente</h1>
          <p className="text-gray-500">
            {selectedTenant
              ? `Usuarios de ${selectedTenant.name}`
              : 'Selecciona un cliente para ver sus usuarios'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tenant List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden sticky top-4">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Clientes</h2>
            </div>
            {loadingTenants ? (
              <div className="p-4 text-center text-gray-500">Cargando...</div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {tenants?.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => setSearchParams({ tenant: tenant.id.toString() })}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                      selectedTenantId === tenant.id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        tenant.is_active
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                          : 'bg-gray-300'
                      }`}>
                        <Building2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${
                          selectedTenantId === tenant.id ? 'text-indigo-700' : 'text-gray-900'
                        }`}>
                          {tenant.name}
                        </p>
                        <p className="text-xs text-gray-500">{tenant.user_count} usuarios</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Users List */}
        <div className="lg:col-span-3">
          {selectedTenantId ? (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Tenant Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">{selectedTenant?.name}</h2>
                      <p className="text-sm text-gray-500">/{selectedTenant?.slug}</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar usuario..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
                    />
                  </div>
                </div>
              </div>

              {/* Users */}
              {loadingUsers ? (
                <div className="p-8 text-center text-gray-500">Cargando usuarios...</div>
              ) : filteredUsers && filteredUsers.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.is_active
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                            : 'bg-gray-300'
                        }`}>
                          <span className="text-white font-bold">
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">{user.full_name}</h3>
                            {!user.is_active && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                Inactivo
                              </span>
                            )}
                            {user.is_locked && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                Bloqueado
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {user.email}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[user.role]}`}>
                            {roleIcons[user.role]}
                            {roleLabels[user.role] || user.role}
                          </span>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Calendar className="h-4 w-4" />
                            {formatDate(user.created_at)}
                          </div>
                          {user.is_locked && (
                            <button
                              onClick={() => handleUnlock(user)}
                              disabled={unlockUser.isPending}
                              className="p-2 hover:bg-green-50 rounded-lg text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Desbloquear cuenta"
                            >
                              <Unlock className="h-4 w-4" />
                            </button>
                          )}
                          {user.role !== 'superadmin' && (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowResetModal(true);
                              }}
                              className="p-2 hover:bg-amber-50 rounded-lg text-amber-500 hover:text-amber-600"
                              title="Resetear contraseña"
                            >
                              <KeyRound className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No se encontraron usuarios</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Selecciona un Cliente</h3>
              <p className="text-gray-500">
                Elige un cliente de la lista para ver sus usuarios
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={closeResetModal}
        title="Resetear Contraseña"
      >
        <div className="space-y-4">
          {!resetResult ? (
            <>
              <p className="text-gray-600">
                ¿Estás seguro de que deseas resetear la contraseña de{' '}
                <strong>{selectedUser?.full_name}</strong>?
              </p>

              <div className="p-4 bg-amber-50 rounded-lg text-sm text-amber-800">
                <strong>Se generará una contraseña temporal</strong> y se enviará por email.
                El usuario deberá cambiarla en su próximo inicio de sesión.
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={closeResetModal}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleResetPassword}
                  loading={resetPassword.isPending}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <KeyRound className="h-4 w-4 mr-2" />
                  Resetear Contraseña
                </Button>
              </div>
            </>
          ) : resetResult.email_sent ? (
            <>
              <div className="p-4 bg-green-50 rounded-lg text-sm text-green-800">
                <p>
                  ✅ Se envió la nueva contraseña temporal a{' '}
                  <strong>{resetResult.user_email}</strong>
                </p>
                <p className="mt-2">
                  El usuario deberá cambiarla en su próximo inicio de sesión.
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={closeResetModal}>Entendido</Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">El email no pudo enviarse</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Debes comunicar esta contraseña manualmente al usuario.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm">
                      {resetResult.user_email}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(resetResult.user_email);
                        toast.success('Email copiado');
                      }}
                      className="p-2 hover:bg-gray-200 rounded"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Contraseña temporal</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                      {resetResult.temporary_password}
                    </code>
                    <button
                      onClick={() => {
                        if (resetResult.temporary_password) {
                          navigator.clipboard.writeText(resetResult.temporary_password);
                          toast.success('Contraseña copiada');
                        }
                      }}
                      className="p-2 hover:bg-gray-200 rounded"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={closeResetModal}>Entendido</Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
